import type { ModuleKey } from "./palettes.generated";

export type { ModuleKey };

/**
 * Route → accent, for the departmental theme.
 *
 * **Order matters; first match wins.** The theme system is HospitalERP's, reused wholesale, and its
 * `ModuleKey` list is fixed by the generated ramps — so HMS maps its own routes onto those existing
 * accents rather than inventing keys, which would mean regenerating every palette.
 *
 * The organising principle is HospitalERP's and it carries over: the accent answers **"what kind of
 * work is this screen"**. Roster, attendance and staff screens are workforce administration, so they
 * wear `administration`. Theatre screens are clinical in-patient work and wear `ip`. The staff
 * self-service portal wears `op` — the neutral blue — because to a nurse checking tomorrow's duty it
 * is not an administrative screen at all.
 */
const RULES: readonly (readonly [RegExp, ModuleKey])[] = [
  // The staff-facing portal is its own world; matched first so nothing below claims it.
  [/^\/me|^\/my\//, "op"],

  // Theatre sits under /workforce but is clinical work, so it must be tested before the
  // general workforce rule swallows it.
  [/^\/workforce\/theatre/, "ip"],

  [/^\/workforce|^\/roster|^\/attendance|^\/leave|^\/workflow/, "administration"],
  [/^\/master|^\/admin|^\/platform/, "administration"],
  [/^\/reports/, "billing"],
] as const;

/**
 * Resolves the accent a path should wear. Falls back to `"op"` so an unmapped screen reads as
 * ordinary rather than as a colour somebody forgot to assign.
 */
export function resolveModule(pathname: string): ModuleKey {
  for (const [pattern, key] of RULES) {
    if (pattern.test(pathname)) return key;
  }
  return "op";
}

/** Human labels, for the theme picker and anywhere the accent needs explaining. */
export const MODULE_LABELS: Record<ModuleKey, string> = {
  billing: "Reports",
  pharmacy: "Pharmacy",
  radiology: "Radiology",
  administration: "Workforce",
  op: "Staff portal",
  lab: "Lab",
  ip: "Theatre",
};
